import React, { useState } from "react";
import { motion } from "framer-motion";
import { FiMapPin, FiPhone, FiMail, FiSend, FiChevronDown } from "react-icons/fi";

const ContactUs = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const FAQ = [
    {
      q: "How quickly will I get a response?",
      a: "Typically within 24 hours during working days.",
    },
    {
      q: "Do you offer customized travel packages?",
      a: "Yes. We design travel plans according to your choice, budget and interest.",
    },
    {
      q: "Can I contact support through phone?",
      a: "Absolutely! Our support team is ready to assist you anytime during business hours.",
    },
  ];

  return (
    <div className="w-full bg-gray-50 text-gray-800">

      {/* ================== Hero Heading ================== */}
      <section className="text-center py-10 px-4">
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl font-bold text-gray-900"
        >
          Get In Touch
        </motion.h1>

        <p className="mt-4 text-gray-600 max-w-2xl mx-auto text-lg">
          We are here to assist and guide you. Reach us through message, call or email — 
          let’s connect and make your experience memorable.
        </p>

        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: "150px" }}
          transition={{ duration: 0.8 }}
          className="h-1 mx-auto bg-blue-500 mt-5 rounded-full"
        />
      </section>


      {/* ================== Main Contact Section ================== */}
      <section className="max-w-7xl bg-blue-50 mx-auto px-6 md:px-12 py-10 grid md:grid-cols-2 gap-12">

        {/* Contact Details */}
        <motion.div 
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="space-y-10"
        >
          <h2 className="text-2xl font-semibold text-gray-900 border-l-4 border-blue-500 pl-3">
            Contact Information
          </h2>

          <div className="space-y-5 text-lg">
            <div className="flex items-start gap-4">
              <FiMapPin className="text-blue-500 text-2xl" />
              <p>119-120 Patni Bazar Jawahar Marg, Ujjain</p>
            </div>

            <div className="flex items-start gap-4">
              <FiMapPin className="text-blue-500 text-2xl" />
              <p>03, Aditya Nagar AB Main Road, Indore MP</p>
            </div>

            <div className="flex items-center gap-4">
              <FiPhone className="text-blue-500 text-2xl" />
              <p>6262725757</p>
            </div>

            <div className="flex items-center gap-4">
              <FiMail className="text-blue-500 text-2xl" />
              <p>info@celestialtours.co.in</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl shadow-md border-l-4 border-blue-500">
            <h3 className="font-semibold text-xl mb-2">Business Hours</h3>
            <p>Mon - Sat: 09:00 AM - 07:00 PM</p>
            <p>Sunday: Closed</p>
          </div>
        </motion.div>


        {/* Contact Form */}
        <motion.form
          initial={{ x: 30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white p-6 md:-mt-3 shadow-lg rounded-xl space-y-6"
        >
          <h2 className="text-2xl font-semibold text-gray-900">Send Us A Message</h2>
          
          <div className="grid md:grid-cols-2 gap-5">
            <input type="text" placeholder="Your Name" className="inputBox" />
            <input type="email" placeholder="Your Email" className="inputBox" />
          </div>

          <input type="text" placeholder="Subject" className="inputBox" />

          <textarea placeholder="Your Message" rows="3" className="inputBox"></textarea>

          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
          >
            <FiSend /> Send Message
          </motion.button>
        </motion.form>
      </section>


      {/* ================== Google Map Embed ================== */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <h2 className="text-2xl font-semibold mb-5">Locate Us On Map</h2>

        <div className="w-full h-[320px] rounded-xl overflow-hidden shadow-lg">
          <iframe
            title="Google Map"
            width="100%"
            height="100%"
            loading="lazy"
            allowFullScreen
            className="rounded-xl"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3567.460189206448!2d75.779467!3d23.176492!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sUjjain%20City!5e0!3m2!1sen!2sin!4v0000000000000"
          ></iframe>
        </div>
      </section>


      {/* ================== Additional Sections ================== */}

      {/* Why Choose Us */}
      <section className="bg-blue-50 py-10">
        <h3 className="text-3xl font-semibold text-center mb-6">Why Choose Us?</h3>
        
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto px-6">
          {["Fast Support", "Customized Tours", "Affordable Pricing"].map((item, i) => (
            <motion.div 
              key={i}
              whileHover={{ scale: 1.05 }}
              className="p-6 bg-white rounded-xl shadow text-center font-medium"
            >
              {item}
            </motion.div>
          ))}
        </div>
      </section>


      {/* FAQ Section */}
      <section className="max-w-5xl mx-auto px-6 py-14">
        <h3 className="text-3xl font-semibold text-center mb-10">Frequently Asked Questions</h3>

        {FAQ.map((item, i) => (
          <div key={i} className="border-b py-3 cursor-pointer" onClick={() => toggleFAQ(i)}>
            <div className="flex justify-between font-semibold">
              {item.q}
              <FiChevronDown className={`transition ${openFAQ === i && "rotate-180"}`} />
            </div>
            {openFAQ === i && <p className="text-gray-600 mt-2">{item.a}</p>}
          </div>
        ))}
      </section>


      {/* Footer CTA */}
      <section className="text-center py-14 bg-blue-50 text-white">
        <h2 className="text-3xl text-gray-800 font-bold">Ready To Plan Your Trip?</h2>
        <p className="mt-3 text-gray-600">Message us anytime — we are happy to assist.</p>

        <button className="mt-5 bg-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-700">
          Contact Our Team
        </button>
      </section>
    </div>
  );
};

export default ContactUs;
